import _regeneratorRuntime from "/home/runner/work/mkt-main-website/mkt-main-website/node_modules/@babel/runtime/regenerator/index.js";
var _marked = /*#__PURE__*/_regeneratorRuntime.mark(traverseGenerator),
  _marked2 = /*#__PURE__*/_regeneratorRuntime.mark(traverseVisibleGenerator),
  _marked3 = /*#__PURE__*/_regeneratorRuntime.mark(traverseAncestorsGenerator);
import "core-js/modules/es.array.sort.js";
import { BufferAttribute, BufferGeometry, Color, Group, Matrix4, Mesh, Vector3 } from 'three';
import { mergeGroups, deepCloneAttribute } from './BufferGeometryUtils.js';
var _color = /*@__PURE__*/new Color();
var _matrix = /*@__PURE__*/new Matrix4();
function createMeshesFromInstancedMesh(instancedMesh) {
  var group = new Group();
  var count = instancedMesh.count;
  var geometry = instancedMesh.geometry;
  var material = instancedMesh.material;
  for (var i = 0; i < count; i++) {
    var mesh = new Mesh(geometry, material);
    instancedMesh.getMatrixAt(i, mesh.matrix);
    mesh.matrix.decompose(mesh.position, mesh.quaternion, mesh.scale);
    group.add(mesh);
  }
  group.copy(instancedMesh);
  group.updateMatrixWorld(); // ensure correct world matrices of meshes

  return group;
}
function createMeshesFromMultiMaterialMesh(mesh) {
  if (Array.isArray(mesh.material) === false) {
    console.warn('THREE.SceneUtils.createMeshesFromMultiMaterialMesh(): The given mesh has no multiple materials.');
    return mesh;
  }
  var object = new Group();
  object.copy(mesh);

  // merge groups (which automatically sorts them)

  var geometry = mergeGroups(mesh.geometry);
  var index = geometry.index;
  var groups = geometry.groups;
  var attributeNames = Object.keys(geometry.attributes);

  // create a mesh for each group by extracting the buffer data into a new geometry

  for (var i = 0; i < groups.length; i++) {
    var group = groups[i];
    var start = group.start;
    var end = start + group.count;
    var newGeometry = new BufferGeometry();
    var newMaterial = mesh.material[group.materialIndex];

    // process all buffer attributes

    for (var j = 0; j < attributeNames.length; j++) {
      var name = attributeNames[j];
      var attribute = geometry.attributes[name];
      var itemSize = attribute.itemSize;
      var newLength = group.count * itemSize;
      var type = attribute.array.constructor;
      var newArray = new type(newLength);
      var newAttribute = new BufferAttribute(newArray, itemSize);
      for (var k = start, n = 0; k < end; k++, n++) {
        var ind = index.getX(k);
        if (itemSize >= 1) newAttribute.setX(n, attribute.getX(ind));
        if (itemSize >= 2) newAttribute.setY(n, attribute.getY(ind));
        if (itemSize >= 3) newAttribute.setZ(n, attribute.getZ(ind));
        if (itemSize >= 4) newAttribute.setW(n, attribute.getW(ind));
      }
      newGeometry.setAttribute(name, newAttribute);
    }
    var newMesh = new Mesh(newGeometry, newMaterial);
    object.add(newMesh);
  }
  return object;
}
function createMultiMaterialObject(geometry, materials) {
  var group = new Group();
  for (var i = 0, l = materials.length; i < l; i++) {
    group.add(new Mesh(geometry, materials[i]));
  }
  return group;
}
function reduceVertices(object, func, initialValue) {
  var value = initialValue;
  var vertex = new Vector3();
  object.updateWorldMatrix(true, true);
  object.traverseVisible(function (child) {
    var geometry = child.geometry;
    if (geometry !== undefined) {
      var position = geometry.attributes.position;
      if (position !== undefined) {
        for (var i = 0, l = position.count; i < l; i++) {
          if (child.isMesh) {
            child.getVertexPosition(i, vertex);
          } else {
            vertex.fromBufferAttribute(position, i);
          }
          if (!child.isSkinnedMesh) {
            vertex.applyMatrix4(child.matrixWorld);
          }
          value = func(value, vertex);
        }
      }
    }
  });
  return value;
}

/**
 * @param {InstancedMesh}
 * @param {function(int, int):int}
 */
function sortInstancedMesh(mesh, compareFn) {
  // store copy of instanced attributes for lookups

  var instanceMatrixRef = deepCloneAttribute(mesh.instanceMatrix);
  var instanceColorRef = mesh.instanceColor ? deepCloneAttribute(mesh.instanceColor) : null;
  var attributeRefs = new Map();
  for (var name in mesh.geometry.attributes) {
    var attribute = mesh.geometry.attributes[name];
    if (attribute.isInstancedBufferAttribute) {
      attributeRefs.set(attribute, deepCloneAttribute(attribute));
    }
  }

  // compute sort order

  var tokens = [];
  for (var i = 0; i < mesh.count; i++) tokens.push(i);
  tokens.sort(compareFn);

  // apply sort order

  for (var _i = 0; _i < tokens.length; _i++) {
    var refIndex = tokens[_i];
    _matrix.fromArray(instanceMatrixRef.array, refIndex * mesh.instanceMatrix.itemSize);
    _matrix.toArray(mesh.instanceMatrix.array, _i * mesh.instanceMatrix.itemSize);
    if (mesh.instanceColor) {
      _color.fromArray(instanceColorRef.array, refIndex * mesh.instanceColor.itemSize);
      _color.toArray(mesh.instanceColor.array, _i * mesh.instanceColor.itemSize);
    }
    for (var _name in mesh.geometry.attributes) {
      var _attribute = mesh.geometry.attributes[_name];
      if (_attribute.isInstancedBufferAttribute) {
        var attributeRef = attributeRefs.get(_attribute);
        _attribute.setX(_i, attributeRef.getX(refIndex));
        if (_attribute.itemSize > 1) _attribute.setY(_i, attributeRef.getY(refIndex));
        if (_attribute.itemSize > 2) _attribute.setZ(_i, attributeRef.getZ(refIndex));
        if (_attribute.itemSize > 3) _attribute.setW(_i, attributeRef.getW(refIndex));
      }
    }
  }
}

/**
 * @param {Object3D} object Object to traverse.
 * @yields {Object3D} Objects that passed the filter condition.
 */
function traverseGenerator(object) {
  var children, i, l;
  return _regeneratorRuntime.wrap(function traverseGenerator$(_context) {
    while (1) switch (_context.prev = _context.next) {
      case 0:
        _context.next = 2;
        return object;
      case 2:
        children = object.children;
        i = 0, l = children.length;
      case 4:
        if (!(i < l)) {
          _context.next = 9;
          break;
        }
        return _context.delegateYield(traverseGenerator(children[i]), "t0", 6);
      case 6:
        i++;
        _context.next = 4;
        break;
      case 9:
      case "end":
        return _context.stop();
    }
  }, _marked);
}

/**
 * @param {Object3D} object Object to traverse.
 * @yields {Object3D} Objects that passed the filter condition.
 */
function traverseVisibleGenerator(object) {
  var children, i, l;
  return _regeneratorRuntime.wrap(function traverseVisibleGenerator$(_context2) {
    while (1) switch (_context2.prev = _context2.next) {
      case 0:
        if (!(object.visible === false)) {
          _context2.next = 2;
          break;
        }
        return _context2.abrupt("return");
      case 2:
        _context2.next = 4;
        return object;
      case 4:
        children = object.children;
        i = 0, l = children.length;
      case 6:
        if (!(i < l)) {
          _context2.next = 11;
          break;
        }
        return _context2.delegateYield(traverseVisibleGenerator(children[i]), "t0", 8);
      case 8:
        i++;
        _context2.next = 6;
        break;
      case 11:
      case "end":
        return _context2.stop();
    }
  }, _marked2);
}

/**
 * @param {Object3D} object Object to traverse.
 * @yields {Object3D} Objects that passed the filter condition.
 */
function traverseAncestorsGenerator(object) {
  var parent;
  return _regeneratorRuntime.wrap(function traverseAncestorsGenerator$(_context3) {
    while (1) switch (_context3.prev = _context3.next) {
      case 0:
        parent = object.parent;
        if (!(parent !== null)) {
          _context3.next = 5;
          break;
        }
        _context3.next = 4;
        return parent;
      case 4:
        return _context3.delegateYield(traverseAncestorsGenerator(parent), "t0", 5);
      case 5:
      case "end":
        return _context3.stop();
    }
  }, _marked3);
}
export { createMeshesFromInstancedMesh, createMeshesFromMultiMaterialMesh, createMultiMaterialObject, reduceVertices, sortInstancedMesh, traverseGenerator, traverseVisibleGenerator, traverseAncestorsGenerator };